// src/utils/cashBook.js
// D-289 Phase 2 — which ledger rows are REAL money movements (Cash / Bank Book).
// D-296 (09 Oct 2026) — opening balance per mode (openingByMode).
//
// WHY THIS EXISTS
// financial_transactions holds bills, project-cost allocations, income and
// payments together. Only some of them are money that actually came in or
// went out:
//   IN  — type "income": written only when money is collected (cash-basis,
//         CORE_RULES §32), so every income row is real money in. A reversal
//         entry (negative amount) reduces it.
//   OUT — type "payment" (D-289): amount = cash that left (net of TDS).
//   OUT — legacy (before D-289): a cost / bill row whose own status was
//         edited to "paid". Counted once, as paid_amount (or amount).
//         Detect-and-fallback — old rows are never edited.
// Everything else (unpaid bills, expense / transport cost rows, material /
// equipment / direct-site allocation rows) is NOT money movement. A bill paid
// after D-289 keeps its old status and gets a NEW payment row, so nothing is
// ever counted twice. This is the ONLY place that decides it.
//
// OPENING BALANCES (D-296): type "opening_balance" rows are money the company
// already had on the go-live date. They are NOT movement in the period, so
// cashMovementOf() ignores them (In / Out stay equal to income / payments);
// openingByMode() adds them to each mode card's starting point instead.

// Payment modes grouped the way money actually lands: NEFT / RTGS / IMPS /
// transfer all hit the bank account.
export const MODE_GROUP = {
  cash: "cash",
  bank: "bank", bank_transfer: "bank", neft: "bank", rtgs: "bank", imps: "bank",
  upi: "upi",
  cheque: "cheque",
};
// Normalised first: "Bank Transfer", "bank-transfer" and "bank_transfer" are the
// same mode (billing screens store labels with spaces and capitals).
export const modeGroupOf = (mode) =>
  MODE_GROUP[String(mode || "").toLowerCase().trim().replace(/[\s-]+/g, "_")] || "other";

// Returns null when the row is not money movement, otherwise
// { direction: "in" | "out", kind, amount, mode, label }.
export function cashMovementOf(row) {
  if (!row) return null;

  // Opening balances are the starting point, not movement (D-296).
  if (row.type === "opening_balance") return null;

  // Money in — every income row (negative = reversal / correction).
  if (row.type === "income") {
    const amount = Number(row.amount || 0);
    if (amount === 0) return null;
    return {
      direction: "in", kind: "income", amount,
      mode: modeGroupOf(row.payment_mode),
      label: row.description || row.client_name || "Payment received",
    };
  }

  // Money out — D-289 payment row.
  if (row.type === "payment") {
    const amount = Number(row.amount || 0);
    if (!(amount > 0) || row.direction === "in") return null;
    const who = row.party_name || "";
    const what = row.description || row.category || "";
    return {
      direction: "out", kind: "payment", amount,
      mode: modeGroupOf(row.payment_mode),
      label: [who, what].filter(Boolean).join(" — ") || "Payment",
    };
  }

  // Money out — legacy row marked "paid" before D-289.
  if (row.status === "paid") {
    const amount = Number(row.paid_amount || row.amount || 0);
    if (!(amount > 0)) return null;
    return {
      direction: "out", kind: "legacy_paid", amount,
      mode: modeGroupOf(row.payment_mode),
      label: row.description || row.vendor_name || row.category || "Payment",
    };
  }

  return null;
}

// Ids of opening entries cancelled by a reversal row (D-296). Shared by the
// Cash / Bank Book and the khaata, so both drop exactly the same entries.
export function reversedOpeningIds(rows = []) {
  const ids = new Set();
  for (const r of rows) {
    if (r && r.type === "opening_balance" && r.entry_kind === "reversal" && r.reverses_id) {
      ids.add(r.reverses_id);
    }
  }
  return ids;
}

// Opening balance per mode (D-296): { cash, bank, upi, cheque, other }.
// side "negative" = overdraft / money owed to the bank on the go-live date.
export function openingByMode(rows = []) {
  const reversed = reversedOpeningIds(rows);
  const b = { cash: 0, bank: 0, upi: 0, cheque: 0, other: 0 };
  for (const r of rows) {
    if (!r || r.type !== "opening_balance" || r.entry_kind !== "mode") continue;
    if (reversed.has(r.id)) continue;
    const amount = Number(r.amount || 0);
    if (!(amount > 0)) continue;
    const sign = r.side === "negative" ? -1 : 1;
    b[modeGroupOf(r.mode_group)] += sign * amount;
  }
  for (const k of Object.keys(b)) b[k] = Math.round(b[k] * 100) / 100;
  return b;
}
